/**
 * THE PROPOSAL'S SHAPE: one row per legacy client, its candidates in the book,
 * and the evidence and reasons per candidate (spec §4.4; research 04 §5, 09
 * §4.4). The matcher proposes and never decides: a certain pair is pre-ticked
 * because its key is unique on both sides of the firm, a likely pair is never
 * pre-ticked, and the admin's decisions are the resolution (`resolution.ts`).
 *
 * BROWSER-SAFE: `effect` only. The view groups' contracts reference these
 * schemas, and a frontend slice imports the contract.
 *
 * NO VALUE IS COPIED INTO THE EVIDENCE. A signal says which field agreed
 * (`email`, `postcode`), never the address or the number, so the proposal
 * carries each client's name and nothing else the firm typed.
 */
import { Schema as S } from "effect"

/** A legacy client's bucket: its best candidate's, where a name-only candidate counts as none. */
export const MATCH_BUCKETS = ["certain", "likely", "none"] as const
export const MatchBucket = S.Literal(...MATCH_BUCKETS).annotations({ identifier: "CutoverMatchBucket" })
export type MatchBucket = typeof MatchBucket.Type

/**
 * One candidate's bucket. `nameOnly` is a folded name with no second signal:
 * shown so the admin can hand-pick it, never proposed as a match (ruling 2).
 */
export const CANDIDATE_BUCKETS = ["certain", "likely", "nameOnly"] as const
export const CandidateBucket = S.Literal(...CANDIDATE_BUCKETS).annotations({ identifier: "CutoverCandidateBucket" })
export type CandidateBucket = typeof CandidateBucket.Type

/**
 * What agreed, or disagreed, between the two clients.
 *
 * - `insightId`: the legacy Insight id equals an active Insight link's id on the book client.
 * - `abn`: the two digit-normalised ABNs are equal.
 * - `name`: the folded names are equal.
 * - `email`, `phone`, `postcode`: a likely signal (research 09 §4.2).
 * - `abnDiffers`: both carry an ABN and they differ (research 00 P09: an incorporation changes it).
 * - `acn`: a nine-digit value in the legacy ABN field equals a book ACN; evidence on a likely pair only.
 */
export const MATCH_EVIDENCE = [
  "insightId",
  "abn",
  "name",
  "email",
  "phone",
  "postcode",
  "abnDiffers",
  "acn",
] as const
export const MatchEvidence = S.Literal(...MATCH_EVIDENCE).annotations({ identifier: "CutoverMatchEvidence" })
export type MatchEvidence = typeof MatchEvidence.Type

/** The two keys that can make a pair certain. */
export const MatchKey = S.Literal("insightId", "abn").annotations({ identifier: "CutoverMatchKey" })
export type MatchKey = typeof MatchKey.Type

/**
 * Why a pair that shares a key is likely and not certain.
 *
 * - `sharedLegacyValue`: the key's value is on `count` of the firm's legacy clients.
 * - `sharedBookValue`: the key's value is on `count` clients in the firm's book.
 * - `keysDisagree`: this legacy client's unique keys point at `count` different book clients.
 * - `bookClientClaimed`: `count` legacy clients reach this book client by a unique key.
 * - `bookClientArchived`: the book client is archived, so a unique key alone is not enough.
 * - `abnDiffers`: the pair shares a unique Steadfast Apps id, but both sides carry an ABN and
 *   they differ. The sync's own client rule calls this the cross-tenant case a person must see
 *   (`sync/outbound/domain/merge/client-match.ts`), so it is never pre-ticked.
 *
 * A certain pair can also carry a `sharedLegacyValue` or `sharedBookValue`
 * reason: its other key is shared, which the screen shows, while the unique key
 * keeps it certain (ruling 2 names each key as sufficient on its own).
 */
export const DEMOTION_CODES = [
  "sharedLegacyValue",
  "sharedBookValue",
  "keysDisagree",
  "bookClientClaimed",
  "bookClientArchived",
  "abnDiffers",
] as const
export const DemotionCode = S.Literal(...DEMOTION_CODES).annotations({ identifier: "CutoverDemotionCode" })
export type DemotionCode = typeof DemotionCode.Type

export const DemotionReason = S.Struct({
  code: DemotionCode,
  /** The key the reason is about; null for the pairing, archive and differing-ABN reasons. */
  key: S.NullOr(MatchKey),
  count: S.Int,
  /** The sentence the screen shows, in plain words. */
  message: S.String,
}).annotations({ identifier: "CutoverDemotionReason" })
export type DemotionReason = typeof DemotionReason.Type

export const MatchCandidate = S.Struct({
  bookId: S.UUID,
  title: S.String,
  archived: S.Boolean,
  bucket: CandidateBucket,
  evidence: S.Array(MatchEvidence),
  reasons: S.Array(DemotionReason),
  /** True for a certain pair only. A likely pair is never pre-ticked (ruling 2). */
  preTicked: S.Boolean,
  /** The candidate's trading name, for the screen only; never part of the fingerprint. Optional so an older answer decodes. */
  tradingName: S.optional(S.NullOr(S.String)),
}).annotations({ identifier: "CutoverMatchCandidate" })
export type MatchCandidate = typeof MatchCandidate.Type

/**
 * WHAT THE REVIEW CAN FILTER ON (build plan 2026-11-03-002, Task 1a): facts
 * every legacy client carries, so the firm (or Cova staff on its instruction)
 * can narrow the list and answer what it shows. Counts, a date, a domain and
 * one Cova CRM id; never an address or a number the firm typed.
 *
 * - `files`, `policies`, `contacts`: how many the legacy client holds.
 * - `createdOn`: the legacy client's creation date, `YYYY-MM-DD`, or null.
 * - `emailDomain`: the domain of its first email, lower-cased, or null.
 * - `namedByCode`: its title, as a code, is some Cova CRM client's Steadfast Apps code.
 * - `codeHolder`: that Cova CRM client, when exactly one holds the code.
 *
 * Facts are never part of a row's fingerprint (`draft-states.ts`): a file
 * added to a client never makes a saved answer ask again.
 */
/** A contact value this legacy client shares with other legacy clients: which kind, and how many hold it. Never the value. */
export const SharedValue = S.Struct({
  key: S.Literal("email", "phone"),
  holders: S.Int,
}).annotations({ identifier: "CutoverSharedValue" })
export type SharedValue = typeof SharedValue.Type

/**
 * Another legacy client of the firm this one may be the same client as
 * (build plan 2026-11-03-002, Task 9; round 2 S13): which keys they share.
 * `twin` is the amber card's rule: a folded name AND a contact value held by
 * at most two legacy clients. Anything less is a fact on the row only.
 */
export const LegacyPeer = S.Struct({
  legacyId: S.UUID,
  keys: S.Array(S.Literal("name", "email", "phone")),
  twin: S.Boolean,
}).annotations({ identifier: "CutoverLegacyPeer" })
export type LegacyPeer = typeof LegacyPeer.Type

export const RowFacts = S.Struct({
  files: S.Int,
  policies: S.Int,
  contacts: S.Int,
  createdOn: S.NullOr(S.String),
  emailDomain: S.NullOr(S.String),
  namedByCode: S.Boolean,
  codeHolder: S.NullOr(S.UUID),
  /** Each email or phone this client shares with 2 or more legacy clients, with the count. */
  sharedValues: S.Array(SharedValue),
  /** Other legacy clients it may be the same client as. */
  peers: S.Array(LegacyPeer),
}).annotations({ identifier: "CutoverRowFacts" })
export type RowFacts = typeof RowFacts.Type

/**
 * The review's groups, strongest first (spec §4.2): each legacy client falls in
 * the first it qualifies for. Derived from the row and its facts, never stored.
 *
 * - `certain`: a certain candidate (Joined for certain).
 * - `copies`: named by a Steadfast Apps code exactly one Cova CRM client holds,
 *   with no files and no policies (Records that look like copies), read from the
 *   facts; no candidate is formed from the code.
 * - `twice`: a twin among the firm's legacy clients (Entered twice).
 * - `crm`: a likely candidate (Already in Cova CRM).
 * - `possible`: a name-only candidate (Possible matches).
 * - `none`: no candidate (No match found).
 */
export const ONBOARDING_GROUPS = ["certain", "copies", "twice", "crm", "possible", "none"] as const
export const OnboardingGroup = S.Literal(...ONBOARDING_GROUPS).annotations({ identifier: "CutoverOnboardingGroup" })
export type OnboardingGroup = typeof OnboardingGroup.Type

/** One legacy client, never one pair: a client in a large book name block has several candidates (research 04 §5). */
export const ProposalRow = S.Struct({
  legacyId: S.UUID,
  title: S.String,
  bucket: MatchBucket,
  candidates: S.Array(MatchCandidate),
  facts: RowFacts,
  /** Its rank in the review, strongest first: certain, likely, a name-only candidate, then none; title, then id. */
  order: S.Int,
  /** Its group in the review (`ONBOARDING_GROUPS`). */
  group: OnboardingGroup,
}).annotations({ identifier: "CutoverProposalRow" })
export type ProposalRow = typeof ProposalRow.Type

export const BucketCounts = S.Struct({ certain: S.Int, likely: S.Int, none: S.Int }).annotations({
  identifier: "CutoverBucketCounts",
})
export type BucketCounts = typeof BucketCounts.Type

export const PairCounts = S.Struct({ certain: S.Int, likely: S.Int, nameOnly: S.Int }).annotations({
  identifier: "CutoverPairCounts",
})
export type PairCounts = typeof PairCounts.Type

/** The whole proposal for one firm. `version` is the resolution's version it is read against. */
export const MatchProposal = S.Struct({
  organizationId: S.UUID,
  version: S.Literal(1),
  legacyClients: S.Int,
  bookClients: S.Int,
  /** Legacy clients per bucket; they sum to `legacyClients`. */
  clients: BucketCounts,
  /** Candidate pairs per candidate bucket. */
  pairs: PairCounts,
  rows: S.Array(ProposalRow),
}).annotations({ identifier: "CutoverMatchProposal" })
export type MatchProposal = typeof MatchProposal.Type
